/**
 * Web API client for BYOS (Bring Your Own Skill).
 * Mirrors the backend routes under /api/v1/{projects|repos}/.../skills.
 */
import type {
  ListSkillsResponse,
  GetSkillResponse,
  UploadSkillResponse,
  ToggleSkillResponse,
  Skill,
  SkillListItem,
  SkillPreview,
} from '../../../../shared/types/skills';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

async function readErr(r: Response, fallback: string): Promise<string> {
  try {
    const j = (await r.json()) as { error?: { message?: string } };
    if (j?.error?.message) return j.error.message;
  } catch {
    /* */
  }
  return fallback;
}

/**
 * Convert a browser File into a base64 payload suitable for the
 * upload routes. ArrayBuffer → btoa-friendly binary string → base64.
 */
export async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// ---------------------------------------------------------------------------
// Project-scope
// ---------------------------------------------------------------------------

export async function listProjectSkills(
  projectId: string,
): Promise<ListSkillsResponse['data']> {
  const r = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/skills`,
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load skills'));
  return ((await r.json()) as ListSkillsResponse).data;
}

export async function uploadProjectSkill(
  projectId: string,
  args: { file: File; name?: string; description?: string },
): Promise<Skill> {
  const contentBase64 = await fileToBase64(args.file);
  const r = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/skills`,
    {
      method: 'POST',
      body: JSON.stringify({
        filename: args.file.name,
        contentBase64,
        name: args.name,
        description: args.description,
      }),
    },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Upload failed'));
  return ((await r.json()) as UploadSkillResponse).data;
}

export async function previewProjectSkill(
  projectId: string,
  slug: string,
): Promise<SkillPreview> {
  const r = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/skills/${encodeURIComponent(slug)}`,
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load skill'));
  return ((await r.json()) as GetSkillResponse).data;
}

export async function toggleProjectSkill(
  projectId: string,
  slug: string,
  active: boolean,
): Promise<Skill> {
  const r = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/skills/${encodeURIComponent(slug)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Toggle failed'));
  return ((await r.json()) as ToggleSkillResponse).data;
}

export async function deleteProjectSkill(
  projectId: string,
  slug: string,
): Promise<void> {
  const r = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/skills/${encodeURIComponent(slug)}`,
    { method: 'DELETE' },
  );
  if (!r.ok && r.status !== 204) throw new Error(await readErr(r, 'Delete failed'));
}

// ---------------------------------------------------------------------------
// Repo-scope (merged list = project skills + repo skills with override flag)
// ---------------------------------------------------------------------------

export async function listRepoSkills(
  org: string,
  repo: string,
): Promise<ListSkillsResponse['data']> {
  const r = await authFetch(
    `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/skills`,
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load skills'));
  return ((await r.json()) as ListSkillsResponse).data;
}

export async function uploadRepoSkill(
  org: string,
  repo: string,
  args: { file: File; name?: string; description?: string },
): Promise<Skill> {
  const contentBase64 = await fileToBase64(args.file);
  const r = await authFetch(
    `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/skills`,
    {
      method: 'POST',
      body: JSON.stringify({
        filename: args.file.name,
        contentBase64,
        name: args.name,
        description: args.description,
      }),
    },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Upload failed'));
  return ((await r.json()) as UploadSkillResponse).data;
}

export async function previewRepoSkill(
  org: string,
  repo: string,
  slug: string,
): Promise<SkillPreview> {
  const r = await authFetch(
    `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/skills/${encodeURIComponent(slug)}`,
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load skill'));
  return ((await r.json()) as GetSkillResponse).data;
}

export async function toggleRepoSkill(
  org: string,
  repo: string,
  slug: string,
  active: boolean,
): Promise<Skill> {
  const r = await authFetch(
    `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/skills/${encodeURIComponent(slug)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    },
  );
  if (!r.ok) throw new Error(await readErr(r, 'Toggle failed'));
  return ((await r.json()) as ToggleSkillResponse).data;
}

export async function deleteRepoSkill(
  org: string,
  repo: string,
  slug: string,
): Promise<void> {
  const r = await authFetch(
    `${API_BASE}/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/skills/${encodeURIComponent(slug)}`,
    { method: 'DELETE' },
  );
  if (!r.ok && r.status !== 204) throw new Error(await readErr(r, 'Delete failed'));
}

// ---------------------------------------------------------------------------
// Re-exports for callers that want stronger typing
// ---------------------------------------------------------------------------
export type { Skill, SkillListItem, SkillPreview };
